import { ChangeEventHandler, ReactNode, useCallback } from 'react';
import { StyledCheckbox, StyledInput, StyledLabel } from './Checkbox.style';
import { CheckmarkIcon } from '@/components/icons';

interface CheckboxProps {
  children?: ReactNode;
  value?: boolean;
  defaultValue?: boolean;
  disabled?: boolean;
  id?: string;
  name?: string;
  onChange?: (value: boolean) => void;
}

/**
 * Checkbox input
 *
 * ```jsx
 * <Checkbox value={value} onChange={onChange} disabled>Checkbox label</Checkbox>
 * ```
 */
export function Checkbox(props: CheckboxProps) {
  const { children, value, defaultValue, onChange, id, name, disabled } = props;

  const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    event => {
      onChange?.(event.target.checked);
    },
    [onChange],
  );

  return (
    <StyledLabel disabled={disabled} data-testid='linui-checkbox-label'>
      <StyledInput
        data-testid='linui-checkbox-input'
        type='checkbox'
        checked={value}
        defaultChecked={defaultValue}
        onChange={handleChange}
        id={id}
        name={name}
        disabled={disabled}
      />
      {/* Wrapping the checkbox so it does not resize when the window size changes */}
      <span>
        <StyledCheckbox disabled={disabled}>
          {value && <CheckmarkIcon size={11} />}
        </StyledCheckbox>
      </span>
      <span>{children}</span>
    </StyledLabel>
  );
}
